import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../components/User_Context.js';
import * as menu from "../../assets/menus/menu.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { SystemLabels } from '../_label/system/system_labels.js';
import { ImportLabels } from '../_label/import_labels/import_labels.js';
import * as uploadManager from "../../components/ui/fileupload.js";

const DataImportScreen = () => {
    const { currentUser } = useContext(UserContext);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const MySwal = withReactContent(Swal);

    // Initialize i18n labels from centralized files
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', SystemLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', SystemLabels.en, true, true);
    i18n.addResourceBundle('it', 'labels', ImportLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', ImportLabels.en, true, true);

    // State management
    const [selectedType, setSelectedType] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [processedData, setProcessedData] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [cleanData, setCleanData] = useState([]);
    const [dirtyData, setDirtyData] = useState([]);
    const [detectedProfile, setDetectedProfile] = useState(null); // For hospitalization profile detection
    const [uploadedFileUID, setUploadedFileUID] = useState(null); // UID from chunked upload
    const [uploadProgress, setUploadProgress] = useState(0); // Upload progress percentage
    
    // Progress tracking state
    const [progressId, setProgressId] = useState(null);
    const [progressData, setProgressData] = useState(null);
    const [isPolling, setIsPolling] = useState(false);
    const [isCurrentlySaving, setIsCurrentlySaving] = useState(false); // Flag to track if we're in save mode
    const saveCompletedRef = useRef(false); // Ref to block all updates after save completes
    
    // Statistics tracking state
    const [importStats, setImportStats] = useState({
        totalRowsInExcel: 0,
        emptyRowsFiltered: 0,
        rowsSentToBackend: 0,
        cleanRowsReceived: 0,
        dirtyRowsReceived: 0,
        backendProcessedTotal: 0,
        missingRows: 0,
        // For hospitalization: events→stays conversion tracking
        validatedEvents: 0,
        rejectedEvents: 0,
        staysGenerated: 0
    });
    
    // Pagination state
    const [cleanDataCurrentPage, setCleanDataCurrentPage] = useState(1);
    const [dirtyDataCurrentPage, setDirtyDataCurrentPage] = useState(1);
    const rowsPerPage = 100;

    // Import types configuration with specific API endpoints
    // Each type calls its dedicated backend processor:
    // antibiotic -> /api/admin/import/antibiotic/{pre-analyze|save}
    // device -> /api/admin/import/device/{pre-analyze|save}  
    // gel -> /api/admin/import/gel/{pre-analyze|save}
    // hospitalization -> /api/admin/import/hospitalization/{pre-analyze|save}
    // 
    // IMPORTANT: Backend expects Italian column names in sourceData and automatically maps them.
    // The expectedColumns show the English equivalents for UI display purposes.
    const importTypes = {
        antibiotic: {
            name: t('antibioticConsumption', { ns: 'labels' }),
            icon: 'fa-pills',
            color: 'primary',
            // Expected Italian columns in Excel file (backend will map these automatically)
            expectedExcelColumns: ['COD_CDC', 'DESCR_CDC', 'COD_ATC', 'DESCR_ATC', 'COD_PRODOTTO', 'DESCR_PRODOTTO', 'UM', 'QUANTITA', 'DDD', 'DDD_UM', 'VALORE', 'ANNO_MESE'],
            // English column names for display and backend parameters
            expectedColumns: ['cdc_code', 'cdc_name', 'atc_code', 'atc_name', 'product_code', 'product_name', 'unit_type', 'qty', 'ddd', 'ddd_um', 'value', 'year_month'],
            requiredFields: ['cdc_code', 'atc_code', 'product_code', 'unit_type', 'qty', 'value', 'year_month']
        },
        device: {
            name: t('medicalDeviceConsumption', { ns: 'labels' }),
            icon: 'fa-stethoscope',
            color: 'success',
            expectedExcelColumns: ['COD_CDC', 'DESCR_CDC', 'COD_CND', 'DESCR_CND', 'COD_PRODOTTO', 'DESCR_PRODOTTO', 'UM', 'QUANTITA', 'VALORE', 'ANNO_MESE'],
            expectedColumns: ['cdc_code', 'cdc_name', 'cnd_code', 'cnd_name', 'product_code', 'product_name', 'unit_type', 'qty', 'value', 'year_month'],
            requiredFields: ['cdc_code', 'cnd_code', 'product_code', 'unit_type', 'qty', 'value', 'year_month']
        },
        gel: {
            name: t('hydroalcoholicGelConsumption', { ns: 'labels' }),
            icon: 'fa-pump-soap',
            color: 'info',
            expectedExcelColumns: ['COD_CDC', 'DESCR_CDC', 'COD_PRODOTTO', 'DESCR_PRODOTTO', 'UM', 'QUANTITA', 'LITRI', 'VALORE', 'ANNO_MESE'],
            expectedColumns: ['cdc_code', 'cdc_name', 'product_code', 'product_name', 'unit_type', 'qty', 'liters', 'value', 'year_month'],
            requiredFields: ['cdc_code', 'product_code', 'unit_type', 'qty', 'liters', 'value', 'year_month']
        },
        hospitalization: {
            name: t('hospitalizationEvents', { ns: 'labels' }),
            icon: 'fa-hospital',
            color: 'warning',            
            expectedExcelColumns: ['COD_AZIENDA', 'PROG_RIC', 'COD_REP_ADT', 'COD_FLUSSO_SIO_COD_UO', 'REGIME_COD', 'REGIME', 'EVENT_TYPE', 'EVENT_DATE', 'ANNO_MESE', 'PROGR_REC'],
            expectedColumns: ['authority_code', 'hospitalization_code', 'cdc_code', 'flow_code', 'hospitalization_type', 'regime_description', 'event_type', 'event_date', 'year_month', 'record_number'],
            requiredFields: ['authority_code', 'hospitalization_code', 'cdc_code', 'hospitalization_type', 'event_type', 'event_date']
        }
    };

    const setBreadCrumb = () => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/admin", name: t('admin', { ns: 'labels' }) },
            { link: "/admin/data_import", name: t('dataImport', { ns: 'labels' }) }
        ];
        breadcrumb.set(path);
    };

    useEffect(() => {
        setBreadCrumb();
        menu.write({ user: currentUser() });
    }, []);

    // Cleanup polling on component unmount
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, []);

    // Handle progress completion
    useEffect(() => {
        if (progressData && (progressData.status === 'completed' || progressData.status === 'error')) {
            setLoading(false);
            
            if (progressData.status === 'error') {
                // Check if this is a save or pre-analysis error
                const isSaveOperation = progressData.taskType?.includes('_save');
                const title = isSaveOperation ? t('importError', { ns: 'labels' }) : t('preAnalysisError', { ns: 'labels' });
                const description = isSaveOperation ? t('finalImportError', { ns: 'labels' }) : t('preAnalysisErrorDescription', { ns: 'labels' });
                
                MySwal.fire({
                    icon: 'error',
                    title: title,
                    html: `
                        <div class="text-start">
                            <p><strong>${description}</strong></p>
                            <div class="alert alert-danger">
                                ${progressData.errors?.[0] || t('unknownError', { ns: 'labels' })}
                            </div>
                        </div>
                    `
                });
            } else if (progressData.status === 'completed') {
                // Check if this is a save operation completion
                const isSaveOperation = progressData.taskType?.includes('_save');
                
                if (isSaveOperation) {
                    // CRITICAL: Set ref IMMEDIATELY to block all future updates
                    saveCompletedRef.current = true;
                    
                    // IMMEDIATELY stop all polling to prevent any further updates
                    stopPolling();
                    
                    // Show success message for final import
                    const importedCount = progressData.finalData?.imported_count || progressData.processedItems || cleanData.length;
                    MySwal.fire({
                        icon: 'success',
                        title: t('importCompleted', { ns: 'labels' }),
                        text: t('recordsImportedSuccessfully', { ns: 'labels' }).replace('{count}', importedCount)
                    });
                    
                    // Reset the save flag
                    setIsCurrentlySaving(false);
                    
                    // DON'T reset form after successful import
                    // Let user see the tables and results
                    // They can upload a new file to start fresh
                }
                // Pre-analysis completion is handled separately in fetchPreAnalysisResults
            }
        }
    }, [progressData]);

    // Auto-scroll to save progress bar when it appears
    useEffect(() => {
        const isSaveInProgress = progressData?.taskType?.includes('_save') && progressData?.status === 'running';
        
        if (isSaveInProgress) {
            // Wait a bit for the DOM to update
            setTimeout(() => {
                const saveProgressBar = document.getElementById('save-progress-bar');
                if (saveProgressBar) {
                    saveProgressBar.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                    });
                }
            }, 300);
        }
    }, [progressData?.taskType, progressData?.status]);

    // Custom file upload using chunking for Excel import
    const uploadExcelFileInChunks = async (file, importType) => {
        return new Promise((resolve, reject) => {
            setLoading(true);
            setUploadProgress(0);
            
            // Map frontend import types to backend IMPORT_TYPES enum
            const importTypeMapping = {
                'antibiotic': 'ANTIBIOTIC',
                'device': 'DEVICES',
                'gel': 'GEL',
                'hospitalization': 'HOSPITALIZATIONS'
            };
            
            const backendImportType = importTypeMapping[importType] || importType.toUpperCase();
            
            // Track upload progress
            let currentProgress = 0;
            const progressInterval = setInterval(() => {
                // Simulate smooth progress increase
                if (currentProgress < 90) {
                    currentProgress += 10;
                    setUploadProgress(currentProgress);
                }
            }, 500);

            // Custom startUpload for import
            api.sendMessage("admin/import/startUpload", {
                user: currentUser(),
                body: {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    lastModifiedDate: file.lastModified,
                    importType: backendImportType
                }
            }).then(async (startResult) => {
                if (!startResult.success) {
                    clearInterval(progressInterval);
                    reject(new Error(startResult.message || 'Failed to start upload'));
                    return;
                }

                const uploadUID = startResult.uid;
                const chunkSize = 1024 * 1024 * 5; // 5MB chunks
                let start = 0;
                let end = chunkSize - 1;

                // Upload file in chunks
                while (start < file.size) {
                    const chunk = file.slice(start, end + 1);
                    const chunk_array = await chunk.arrayBuffer();
                    const chunk_data = Array.from(new Uint8Array(chunk_array));

                    const chunkResult = await api.sendMessage("admin/import/chunkUpload", {
                        user: currentUser(),
                        body: {
                            uid: uploadUID,
                            data: chunk_data,
                            last: end >= file.size
                        }
                    });

                    if (!chunkResult.success) {
                        clearInterval(progressInterval);
                        reject(new Error(chunkResult.message || 'Chunk upload failed'));
                        return;
                    }

                    // Update progress based on actual bytes uploaded
                    const actualProgress = Math.min(90, Math.trunc((start / file.size) * 90));
                    setUploadProgress(actualProgress);

                    start += chunkSize;
                    end += chunkSize;
                }

                clearInterval(progressInterval);
                setUploadProgress(100);

                resolve({
                    uid: uploadUID,
                    fileName: file.name,
                    fileSize: file.size
                });
            }).catch(error => {
                clearInterval(progressInterval);
                reject(error);
            });
        });
    };

    // File handling functions
    const handleFileUpload = async (file) => {
        if (!selectedType) {
            MySwal.fire({
                icon: 'warning',
                title: t('attention', { ns: 'labels' }),
                text: t('selectTypeFirst', { ns: 'labels' })
            });
            return;
        }

        if (!file.name.match(/\.(xlsx|xls)$/)) {
            MySwal.fire({
                icon: 'error',
                title: t('invalidFileFormat', { ns: 'labels' }),
                text: t('selectExcelFile', { ns: 'labels' })
            });
            return;
        }

        setLoading(true);
        setUploadedFile(file);
        setUploadProgress(0);

        try {
            // Upload file in chunks to backend
            const uploadResult = await uploadExcelFileInChunks(file, selectedType);
            
            // Store the uploaded file UID
            setUploadedFileUID(uploadResult.uid);
            
            // Request preview data from backend
            const previewResult = await api.sendMessage(`admin/import/${selectedType}/preview`, {
                user: currentUser(),
                body: {
                    uid: uploadResult.uid,
                    rows: 10 // Request first 10 rows for preview
                }
            });
            
            if (!previewResult.success) {
                throw new Error(previewResult.message || t('previewLoadError', { ns: 'labels' }));
            }
            
            // Set preview data and statistics
            setPreviewData(previewResult.preview);
            setParsedData({ uid: uploadResult.uid }); // Store minimal data, actual data is on server
            
            // Update statistics
            if (previewResult.statistics) {
                setImportStats(prev => ({
                    ...prev,
                    totalRowsInExcel: previewResult.statistics.totalRows || 0,
                    emptyRowsFiltered: previewResult.statistics.emptyRows || 0,
                    rowsSentToBackend: previewResult.statistics.validRows || 0
                }));
            }
            
        } catch (error) {
            MySwal.fire({
                icon: 'error',
                title: t('error', { ns: 'labels' }),
                text: error.message || t('excelUploadError', { ns: 'labels' })
            });
            setUploadedFile(null);
            setUploadedFileUID(null);
            setUploadProgress(0);
        } finally {
            setLoading(false);
        }
    };

    // Backend validation: Call backend to validate Excel columns
    const validateColumnsWithBackend = async (data, importType) => {
        try {
            const endpoint = `admin/import/${importType}/validate-columns`;
            
            const response = await api.sendMessage(endpoint, {
                user: currentUser(),
                body: {
                    sourceData: data,
                    importType: importType
                }
            });
            
            return response;
        } catch (error) {
            return {
                success: false,
                error: {
                    code: 'VALIDATION_REQUEST_FAILED',
                    message: error.message || t('validationRequestFailed', { ns: 'labels' }),
                    type: 'CONNECTION_ERROR'
                }
            };
        }
    };

    // Format Excel serial date numbers to readable dates
    const formatExcelDate = (value) => {
        // Check if value is an Excel serial date number
        if (typeof value === 'number' && value > 0 && value < 100000) {
            // Excel serial date: days since 1900-01-01
            const excelEpoch = new Date(Date.UTC(1899, 11, 30));
            const msPerDay = 24 * 60 * 60 * 1000;
            const date = new Date(excelEpoch.getTime() + (value * msPerDay));
            
            // Return formatted date string
            return date.toLocaleDateString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
        
        return value;
    };

    // Parse Excel file using xlsx library
    const parseExcelFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    
                    // Get the first worksheet
                    const worksheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[worksheetName];
                    
                    // Convert to JSON with headers from first row
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                        header: 1,  // Use first row as headers
                        defval: ''  // Default value for empty cells
                    });
                    
                    if (jsonData.length === 0) {
                        reject(new Error(t('emptyExcelFile', { ns: 'labels' })));
                        return;
                    }
                    
                    // First row contains headers, rest are data
                    const headers = jsonData[0];
                    const rows = jsonData.slice(1);
                    
                    // Convert to array of objects
                    const parsedData = rows.map(row => {
                        const obj = {};
                        headers.forEach((header, index) => {
                            // Clean header name (remove spaces, convert to lowercase)
                            const cleanHeader = String(header).trim();
                            obj[cleanHeader] = row[index] !== undefined ? row[index] : '';
                        });
                        return obj;
                    });
                    
                    // Filter out completely empty rows
                    const filteredData = parsedData.filter(row => 
                        Object.values(row).some(value => value !== '' && value !== null && value !== undefined)
                    );
                    
                    const emptyRows = rows.length - filteredData.length;
                    
                    // Update statistics
                    setImportStats(prev => ({
                        ...prev,
                        totalRowsInExcel: rows.length,
                        emptyRowsFiltered: emptyRows,
                        rowsSentToBackend: filteredData.length
                    }));
                    
                    resolve(filteredData);
                } catch (error) {
                    reject(new Error(t('excelParsingError', { ns: 'labels' }) + ': ' + error.message));
                }
            };
            
            reader.onerror = () => {
                reject(new Error(t('fileReadError', { ns: 'labels' })));
            };
            
            reader.readAsArrayBuffer(file);
        });
    };


    // Progress polling functions
    const pollProgress = async (progressId) => {
        try {
            const response = await api.sendGetRequest(`admin/import/progress/${progressId}`, {
                user: currentUser(),
                silentMode: true // Prevent automatic error popups during polling
            });

            if (response.success && response.data) {
                setProgressData(response.data);
                
                // Update warnings in real-time if available
                if (response.data.warnings && response.data.warnings.length > 0) {
                    setWarnings(response.data.warnings);
                }

                // Check if polling should stop
                if (response.data.status === 'completed' || response.data.status === 'error') {
                    setIsPolling(false);
                    
                    // If completed, try to get final results ONLY for pre-analysis, NOT for save
                    if (response.data.status === 'completed' && !isCurrentlySaving) {
                        // Give a small delay to ensure backend has finalized everything
                        setTimeout(() => {
                            checkForFinalResults(progressId);
                        }, 1000);
                    }
                    
                    return false; // Stop polling
                }
                
                return true; // Continue polling
            }
            
            return false; // Stop polling on error
        } catch (error) {
            // Check for specific error types
            if (error.message && (error.message.includes('403') || error.message.includes('500'))) {
                // For 500 errors, show a different message
                if (error.message.includes('500')) {
                    MySwal.fire({
                        icon: 'warning',
                        title: t('progressTrackingError', { ns: 'labels' }) || 'Progress Tracking Error',
                        text: t('progressTrackingErrorDescription', { ns: 'labels' }) || 'There was an error with progress tracking. The import will continue in the background.',
                        timer: 4000,
                        showConfirmButton: false
                    });
                } else {
                    MySwal.fire({
                        icon: 'info',
                        title: t('progressNotAvailable', { ns: 'labels' }) || 'Progress Tracking Not Available',
                        text: t('progressNotAvailableDescription', { ns: 'labels' }) || 'Real-time progress tracking is not available. The import will continue in the background.',
                        timer: 3000,
                        showConfirmButton: false
                    });
                }
            }
            
            // Don't immediately stop polling for server errors - keep showing progress bar
            // but update the progress data to show the error state
            setProgressData(prevData => ({
                ...prevData,
                status: 'running',
                currentStep: t('progressTrackingUnavailable', { ns: 'labels' }) || 'Progress tracking unavailable - import continuing in background...',
                percentage: prevData?.percentage || 0
            }));
            
            setIsPolling(false);
            return false; // Stop polling on error
        }
    };

    // Function to check for final results when import is completed
    const checkForFinalResults = async (progressId) => {
        // Block all updates if save has completed
        if (saveCompletedRef.current) {
            return;
        }
        
        try {
            // Use the isCurrentlySaving flag to determine if we should skip data updates
            const isSaveOperation = isCurrentlySaving;
            
            const endpoint = `admin/import/${selectedType}/results/${progressId}`;
            const response = await api.sendGetRequest(endpoint, {
                user: currentUser(),
                silentMode: true
            });
            
            if (response.success && response.intermediateData) {
                setLoading(false);
                
                // Only update cleanData and dirtyData for PRE-ANALYSIS, not for SAVE
                // During save, cleanData and dirtyData are already correct from pre-analysis
                if (!isSaveOperation) {
                    // Process the results
                    setProcessedData(response.intermediateData);
                    setWarnings(response.warnings || []);
                    
                    // Separate clean and dirty data
                    const clean = [];
                    const dirty = [];
                    
                    // Group warnings by row number to avoid duplicates
                    const warningsByRow = {};
                    response.warnings?.forEach(warning => {
                        if (warning.fault) {
                            const rowNum = warning.row_number;
                            if (!warningsByRow[rowNum]) {
                                warningsByRow[rowNum] = {
                                    row: rowNum,
                                    data: warning.row_object,
                                    errors: [],
                                    codes: []
                                };
                            }
                            warningsByRow[rowNum].errors.push(warning.warn_description);
                            warningsByRow[rowNum].codes.push(warning.warn_code);
                        }
                    });

                    // Convert grouped warnings to dirty data array
                    Object.values(warningsByRow).forEach(warningGroup => {
                        dirty.push({
                            row: warningGroup.row,
                            data: warningGroup.data,
                            error: warningGroup.errors.join('; '), // Combine multiple errors
                            code: warningGroup.codes.join(', ') // Combine multiple codes
                        });
                    });

                    // Clean data is simply the intermediateData (already validated records)
                    // The backend already filtered out invalid records, so intermediateData contains only clean records
                    if (response.intermediateData && Array.isArray(response.intermediateData)) {
                        clean.push(...response.intermediateData);
                    }
                    
                    // Use backend statistics if available (for hospitalization)
                    let backendTotal, missing;
                    if (response.statistics && response.statistics.total_processed) {
                        backendTotal = response.statistics.total_processed;
                        missing = importStats.rowsSentToBackend - backendTotal;
                    } else {
                        backendTotal = clean.length + dirty.length;
                        missing = importStats.rowsSentToBackend - backendTotal;
                    }
                    
                    // Update statistics with final counts and backend stats
                    setImportStats(prev => ({
                        ...prev,
                        cleanRowsReceived: clean.length,
                        dirtyRowsReceived: dirty.length,
                        backendProcessedTotal: backendTotal,
                        missingRows: missing,
                        // For hospitalization: add events information
                        ...(response.statistics && {
                            validatedEvents: response.statistics.validated_events,
                            rejectedEvents: response.statistics.rejected_events,
                            staysGenerated: response.statistics.stays_generated
                        })
                    }));

                    setCleanData(clean);
                    setDirtyData(dirty);
                }
                // For save operations, keep existing data from pre-analysis
            } else {
                // Final results not yet available, will try fallback method
            }
        } catch (error) {
            // Error fetching final results
        }
    };

    const startPolling = (progressId) => {
        setIsPolling(true);
        setProgressId(progressId);
        
        // Set initial progress data to show the progress bar immediately
        setProgressData({
            taskId: progressId,
            status: 'running',
            percentage: 0,
            processedItems: 0,
            totalItems: 0,
            currentStep: t('initializingAnalysis', { ns: 'labels' }) || 'Initializing analysis...',
            startTime: new Date().toISOString(),
            warnings: [],
            errors: []
        });
        
        // Start polling immediately, don't wait for initial test
        // This ensures the progress bar shows right away
        const pollInterval = setInterval(async () => {
            const shouldContinue = await pollProgress(progressId);
            
            if (!shouldContinue) {
                clearInterval(pollInterval);
                setIsPolling(false);
                setLoading(false); // Ensure loading is stopped when polling ends
            }
        }, 1000); // Poll every second

        // Store the interval ID for cleanup
        window.currentPollInterval = pollInterval;
        
        // Do an immediate poll to get initial data, but don't block the UI
        pollProgress(progressId).catch(error => {
            // Initial poll failed, but continuing with interval polling
        });
        
        // Set up a fallback mechanism to check for completion even if progress polling fails
        const fallbackInterval = setInterval(async () => {
            // Block all updates if save has completed
            if (saveCompletedRef.current) {
                clearInterval(fallbackInterval);
                return;
            }
            
            if (!isPolling) {
                clearInterval(fallbackInterval);
                return;
            }
            
            // Skip fallback checks during save operations
            if (isCurrentlySaving) {
                return;
            }
            
            try {
                // Try to get the final results to see if the import completed
                const endpoint = `admin/import/${selectedType}/results/${progressId}`;
                const response = await api.sendGetRequest(endpoint, {
                    user: currentUser(),
                    silentMode: true
                });
                
                // Use the isCurrentlySaving flag to determine if we should skip data updates
                const isSaveOperation = isCurrentlySaving;
                
                if (response.success && response.intermediateData) {
                    clearInterval(fallbackInterval);
                    setIsPolling(false);
                    setLoading(false);
                    
                    // Only update cleanData and dirtyData for PRE-ANALYSIS, not for SAVE
                    // During save, cleanData and dirtyData are already correct from pre-analysis
                    if (!isSaveOperation) {
                        // Process the results
                        setProcessedData(response.intermediateData);
                        setWarnings(response.warnings || []);
                        
                        // Separate clean and dirty data
                        const clean = [];
                        const dirty = [];
                        
                        // Group warnings by row number to avoid duplicates
                        const warningsByRow = {};
                        response.warnings?.forEach(warning => {
                            if (warning.fault) {
                                const rowNum = warning.row_number;
                                if (!warningsByRow[rowNum]) {
                                    warningsByRow[rowNum] = {
                                        row: rowNum,
                                        data: warning.row_object,
                                        errors: [],
                                        codes: []
                                    };
                                }
                                warningsByRow[rowNum].errors.push(warning.warn_description);
                                warningsByRow[rowNum].codes.push(warning.warn_code);
                            }
                        });

                        // Convert grouped warnings to dirty data array
                        Object.values(warningsByRow).forEach(warningGroup => {
                            dirty.push({
                                row: warningGroup.row,
                                data: warningGroup.data,
                                error: warningGroup.errors.join('; '), // Combine multiple errors
                                code: warningGroup.codes.join(', ') // Combine multiple codes
                            });
                        });

                        // Clean data is simply the intermediateData (already validated records)
                        // The backend already filtered out invalid records, so intermediateData contains only clean records
                        if (response.intermediateData && Array.isArray(response.intermediateData)) {
                            clean.push(...response.intermediateData);
                        }
                        
                        const backendTotal = (response.statistics?.total_processed) || (clean.length + dirty.length);
                        const missing = importStats.rowsSentToBackend - backendTotal;
                        
                        // Update statistics with final counts
                        setImportStats(prev => ({
                            ...prev,
                            cleanRowsReceived: clean.length,
                            dirtyRowsReceived: dirty.length,
                            backendProcessedTotal: backendTotal,
                            missingRows: missing
                        }));

                        setCleanData(clean);
                        setDirtyData(dirty);
                    }
                    // For save operations, keep existing data from pre-analysis
                    
                    // Update progress to show completion
                    setProgressData(prevData => ({
                        ...prevData,
                        status: 'completed',
                        percentage: 100,
                        currentStep: isSaveOperation 
                            ? (t('importCompleted', { ns: 'labels' }) || 'Import completed successfully')
                            : (t('processingCompleted', { ns: 'labels' }) || t('analysisCompleted', { ns: 'labels' }) || 'Processing completed successfully'),
                        endTime: new Date().toISOString()
                    }));
                }
            } catch (error) {
                // Silently ignore errors in fallback check
            }
        }, 5000); // Check every 5 seconds
        
        // Store fallback interval for cleanup
        window.currentFallbackInterval = fallbackInterval;
    };

    const stopPolling = () => {
        if (window.currentPollInterval) {
            clearInterval(window.currentPollInterval);
            window.currentPollInterval = null;
        }
        if (window.currentFallbackInterval) {
            clearInterval(window.currentFallbackInterval);
            window.currentFallbackInterval = null;
        }
        setIsPolling(false);
        setProgressId(null);
        setProgressData(null);
    };

    // API call for data pre-analysis using specific endpoints
    const preAnalyzeData = async () => {
        if (!uploadedFileUID || !selectedType) {
            MySwal.fire({
                icon: 'warning',
                title: t('attention', { ns: 'labels' }),
                text: t('uploadFileFirst', { ns: 'labels' })
            });
            return;
        }

        setLoading(true);
        setIsCurrentlySaving(false); // Ensure flag is false for pre-analysis
        saveCompletedRef.current = false; // Reset the ref for new pre-analysis
        
        // Reset progress state and pagination
        stopPolling();
        setCleanDataCurrentPage(1);
        setDirtyDataCurrentPage(1);
        
        try {
            // Use specific endpoint based on selected type
            const endpoint = `admin/import/${selectedType}/pre-analyze`;
            
            // CRITICAL: Backend uses uploaded file UID to access the Excel data
            // For hospitalization: backend auto-detects profile and handles column mapping
            // For other types: backend expects Italian column names and maps them internally
            const parameters = {
                enableProgress: true
            };
            
            // Only add columns parameter for non-hospitalization types
            // Hospitalization uses multi-profile auto-detection
            if (selectedType !== 'hospitalization') {
                parameters.columns = importTypes[selectedType].expectedColumns;
            }
            
            const response = await api.sendMessage(endpoint, {
                user: currentUser(),
                body: {
                    uid: uploadedFileUID, // Send file UID instead of parsed data
                    parameters: parameters
                }
            });

            if (response.success) {
                // Check if we have a progressId for tracking
                if (response.progressId) {
                    // Start polling for progress updates
                    startPolling(response.progressId);
                } else {
                    // No progress tracking, process results immediately
                    setLoading(false);
                }
                
                // Store detected profile for hospitalization (if provided by backend)
                if (selectedType === 'hospitalization' && response.detectedProfile) {
                    setDetectedProfile(response.detectedProfile);
                }
                
                setProcessedData(response.intermediateData);
                setWarnings(response.warnings || []);
                
                // Separate clean and dirty data
                const clean = [];
                const dirty = [];
                
                // Group warnings by row number to avoid duplicates
                const warningsByRow = {};
                response.warnings?.forEach(warning => {
                    if (warning.fault) {
                        const rowNum = warning.row_number;
                        if (!warningsByRow[rowNum]) {
                            warningsByRow[rowNum] = {
                                row: rowNum,
                                data: warning.row_object,
                                errors: [],
                                codes: []
                            };
                        }
                        warningsByRow[rowNum].errors.push(warning.warn_description);
                        warningsByRow[rowNum].codes.push(warning.warn_code);
                    }
                });

                // Convert grouped warnings to dirty data array
                Object.values(warningsByRow).forEach(warningGroup => {
                    dirty.push({
                        row: warningGroup.row,
                        data: warningGroup.data,
                        error: warningGroup.errors.join('; '), // Combine multiple errors
                        code: warningGroup.codes.join(', ') // Combine multiple codes
                    });
                });

                // Clean data is simply the intermediateData (already validated records)
                // The backend already filtered out invalid records, so intermediateData contains only clean records
                if (response.intermediateData && Array.isArray(response.intermediateData)) {
                    clean.push(...response.intermediateData);
                }
                
                const backendTotal = (response.statistics?.total_processed) || (clean.length + dirty.length);
                const missing = importStats.rowsSentToBackend - backendTotal;
                
                // Update statistics with final counts (only if not using progress tracking)
                if (!response.progressId) {
                    setImportStats(prev => ({
                        ...prev,
                        cleanRowsReceived: clean.length,
                        dirtyRowsReceived: dirty.length,
                        backendProcessedTotal: backendTotal,
                        missingRows: missing
                    }));
                }

                setCleanData(clean);
                setDirtyData(dirty);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: t('preAnalysisError', { ns: 'labels' }),
                    html: `
                        <div class="text-start">
                            <p><strong>${t('preAnalysisErrorDescription', { ns: 'labels' })}</strong></p>
                            <div class="alert alert-danger">
                                ${response.message || t('unknownPreAnalysisError', { ns: 'labels' })}
                            </div>
                            <small class="text-muted">
                                ${t('verifyColumnsData', { ns: 'labels' })}
                            </small>
                        </div>
                    `
                });
            }
        } catch (error) {
            stopPolling(); // Stop polling on error
            MySwal.fire({
                icon: 'error',
                title: t('connectionError', { ns: 'labels' }),
                html: `
                    <div class="text-start">
                        <p><strong>${t('connectionErrorDescription', { ns: 'labels' })}</strong></p>
                        <div class="alert alert-warning">
                            ${error.message || t('serverConnectionError', { ns: 'labels' })}
                        </div>
                        <small class="text-muted">
                            ${t('checkConnectionRetry', { ns: 'labels' })}
                        </small>
                    </div>
                `
            });
        } finally {
            // Only set loading to false if we're not starting polling
            if (!isPolling) {
                setLoading(false);
            }
        }
    };

    // Final import of clean data using specific endpoints
    const importCleanData = async () => {
        if (!cleanData.length) {
            MySwal.fire({
                icon: 'warning',
                title: t('attention', { ns: 'labels' }),
                text: t('noDataToImport', { ns: 'labels' })
            });
            return;
        }

        const result = await MySwal.fire({
            title: t('confirmImport', { ns: 'labels' }),
            text: t('confirmImportMessage', { ns: 'labels' }).replace('{count}', cleanData.length),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('importData', { ns: 'labels' }),
            cancelButtonText: t('cancel', { ns: 'labels' })
        });

        if (result.isConfirmed) {
            setLoading(true);
            setIsCurrentlySaving(true); // Set flag to indicate we're saving
            try {
                // Use specific save endpoint based on selected type
                const endpoint = `admin/import/${selectedType}/save`;
                
                // Send cleanData directly - backend will skip pre-analysis
                // This is MUCH faster since pre-analysis was already done
                const parameters = {};
                
                // Only add columns parameter for non-hospitalization types
                if (selectedType !== 'hospitalization') {
                    parameters.columns = importTypes[selectedType].expectedColumns;
                }
                
                const response = await api.sendMessage(endpoint, {
                    user: currentUser(),
                    body: {
                        cleanData: cleanData, // Send pre-validated clean data directly
                        parameters: parameters
                    }
                });

                // Save is async - handle progressId
                if (response.success && response.progressId) {
                    setProgressId(response.progressId);
                    setProgressData({ 
                        status: 'running', 
                        percentage: 0, 
                        processedItems: 0, 
                        totalItems: cleanData.length,
                        currentStep: 'Starting import...',
                        taskType: 'import_save'
                    });
                    startPolling(response.progressId);
                    
                    // Scroll to progress bar for better visibility
                    setTimeout(() => {
                        const progressCard = document.querySelector('.card.border-success');
                        if (progressCard) {
                            progressCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }, 100);
                    
                    // Loading will be set to false when progress completes
                } else {
                    setLoading(false);
                    MySwal.fire({
                        icon: 'error',
                        title: t('importError', { ns: 'labels' }),
                        html: `
                            <div class="text-start">
                                <p><strong>${t('finalImportError', { ns: 'labels' })}</strong></p>
                                <div class="alert alert-danger">
                                    ${response.message || t('unknownImportError', { ns: 'labels' })}
                                </div>
                                <small class="text-muted">
                                    ${t('dataNotSaved', { ns: 'labels' })}
                                </small>
                            </div>
                        `
                    });
                }
            } catch (error) {
                setLoading(false);
                MySwal.fire({
                    icon: 'error',
                    title: t('connectionError', { ns: 'labels' }),
                    html: `
                        <div class="text-start">
                            <p><strong>${t('importConnectionError', { ns: 'labels' })}</strong></p>
                            <div class="alert alert-warning">
                                ${error.message || t('serverConnectionError', { ns: 'labels' })}
                            </div>
                            <small class="text-muted">
                                ${t('importMayNotBeComplete', { ns: 'labels' })}
                            </small>
                        </div>
                    `
                });
            } finally {
                setLoading(false);
            }
        }
    };

    const resetForm = () => {
        stopPolling(); // Stop any active polling
        setSelectedType('');
        setUploadedFile(null);
        setParsedData(null);
        setPreviewData(null);
        setProcessedData(null);
        setWarnings([]);
        setCleanData([]);
        setDirtyData([]);
        setProgressData(null);
        setProgressId(null);
        setDetectedProfile(null);
        setUploadedFileUID(null);
        setUploadProgress(0);
        // Reset pagination
        setCleanDataCurrentPage(1);
        setDirtyDataCurrentPage(1);
        // Reset statistics
        setImportStats({
            totalRowsInExcel: 0,
            emptyRowsFiltered: 0,
            rowsSentToBackend: 0,
            cleanRowsReceived: 0,
            dirtyRowsReceived: 0,
            backendProcessedTotal: 0,
            missingRows: 0,
            validatedEvents: 0,
            rejectedEvents: 0,
            staysGenerated: 0
        });
    };

    // Download dirty data as Excel file
    const downloadDirtyDataAsExcel = () => {
        if (!dirtyData.length) {
            MySwal.fire({
                icon: 'info',
                title: t('noDataToDownload', { ns: 'labels' }),
                text: t('noErrorDataAvailable', { ns: 'labels' })
            });
            return;
        }

        try {
            // Excel cell character limit
            const EXCEL_CELL_LIMIT = 32767;
            
            // Helper function to truncate text to Excel's limit
            const truncateForExcel = (value) => {
                if (value === null || value === undefined) {
                    return '';
                }
                const stringValue = String(value);
                if (stringValue.length > EXCEL_CELL_LIMIT) {
                    return stringValue.substring(0, EXCEL_CELL_LIMIT - 50) + '\n\n[... TRONCATO ...]';
                }
                return stringValue;
            };
            
            // Helper function to check if a field name is a date field
            const isDateField = (fieldName) => {
                const lowerField = String(fieldName).toLowerCase();
                return lowerField.includes('date') || lowerField.includes('data') || 
                       lowerField === 'event_date' || lowerField === 'admission_date' || 
                       lowerField === 'discharge_date';
            };
            
            // Prepare data for Excel export
            const excelData = dirtyData.map((errorItem, index) => {
                const row = {
                    [t('rowNumber', { ns: 'labels' })]: errorItem.row,
                    [t('errorCode', { ns: 'labels' })]: truncateForExcel(errorItem.code || ''),
                    [t('errorDescription', { ns: 'labels' })]: truncateForExcel(errorItem.error || '')
                };

                // Add original data columns if available
                if (errorItem.data && typeof errorItem.data === 'object') {
                    Object.keys(errorItem.data).forEach(key => {
                        try {
                            const value = errorItem.data[key];
                            
                            // Handle different value types safely
                            if (value === null || value === undefined || value === '') {
                                row[key] = '';
                            } else if (value instanceof Date) {
                                // Convert Date objects to string
                                row[key] = value.toLocaleDateString('it-IT', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                });
                            } else if (isDateField(key) && typeof value === 'number') {
                                // Format Excel serial date numbers
                                row[key] = formatExcelDate(value);
                            } else if (typeof value === 'object') {
                                // Convert complex objects to JSON string (truncated)
                                const jsonString = JSON.stringify(value);
                                row[key] = truncateForExcel(jsonString);
                            } else {
                                // Convert to string and truncate to ensure XLSX compatibility
                                row[key] = truncateForExcel(value);
                            }
                        } catch (error) {
                            // If any error occurs with a specific field, use empty string
                            row[key] = '';
                        }
                    });
                }

                return row;
            });

            // Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // Set column widths for better readability
            const columnWidths = [];
            if (excelData.length > 0) {
                Object.keys(excelData[0]).forEach(key => {
                    const maxLength = Math.max(
                        key.length,
                        ...excelData.map(row => String(row[key] || '').length)
                    );
                    columnWidths.push({ wch: Math.min(maxLength + 2, 50) });
                });
            }
            worksheet['!cols'] = columnWidths;

            // Add worksheet to workbook
            const sheetName = `${importTypes[selectedType].name} - ${t('errors', { ns: 'labels' })}`;
            // Truncate sheet name to Excel's 31-character limit
            const truncatedSheetName = sheetName.length > 31 ? sheetName.substring(0, 31) : sheetName;
            XLSX.utils.book_append_sheet(workbook, worksheet, truncatedSheetName);

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const filename = `${selectedType}_errors_${timestamp}.xlsx`;

            // Download the file
            XLSX.writeFile(workbook, filename);

            // Show success message
            MySwal.fire({
                icon: 'success',
                title: t('downloadCompleted', { ns: 'labels' }),
                text: t('errorDataDownloaded', { ns: 'labels' }).replace('{filename}', filename),
                timer: 3000,
                showConfirmButton: false
            });

        } catch (error) {
            MySwal.fire({
                icon: 'error',
                title: t('downloadError', { ns: 'labels' }),
                text: t('errorGeneratingFile', { ns: 'labels' }),
                footer: `<small>Dettaglio tecnico: ${error.message}</small>`
            });
        }
    };

    // Pagination utility functions
    const getPaginatedData = (data, currentPage, itemsPerPage) => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return data.slice(startIndex, endIndex);
    };

    const getTotalPages = (totalItems, itemsPerPage) => {
        return Math.ceil(totalItems / itemsPerPage);
    };

    const renderPagination = (currentPage, totalPages, onPageChange, dataType) => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;
        
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return (
            <nav aria-label={`${dataType} pagination`}>
                <ul className="pagination pagination-sm justify-content-center mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                            className="page-link" 
                            onClick={() => onPageChange(1)}
                            disabled={currentPage === 1}
                        >
                            <i className="fa fa-angle-double-left"></i>
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                            className="page-link" 
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <i className="fa fa-angle-left"></i>
                        </button>
                    </li>
                    
                    {startPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => onPageChange(1)}>1</button>
                            </li>
                            {startPage > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}
                    
                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button 
                                className="page-link" 
                                onClick={() => onPageChange(page)}
                            >
                                {page}
                            </button>
                        </li>
                    ))}
                    
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button className="page-link" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
                            </li>
                        </>
                    )}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                            className="page-link" 
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            <i className="fa fa-angle-right"></i>
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                            className="page-link" 
                            onClick={() => onPageChange(totalPages)}
                            disabled={currentPage === totalPages}
                        >
                            <i className="fa fa-angle-double-right"></i>
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    // Download clean data as Excel file
    const downloadCleanDataAsExcel = () => {
        if (!cleanData.length) {
            MySwal.fire({
                icon: 'info',
                title: t('noDataToDownload', { ns: 'labels' }),
                text: t('noCleanDataAvailable', { ns: 'labels' })
            });
            return;
        }

        try {
            // Excel cell character limit
            const EXCEL_CELL_LIMIT = 32767;
            
            // Helper function to truncate text to Excel's limit
            const truncateForExcel = (value) => {
                if (value === null || value === undefined) {
                    return '';
                }
                const stringValue = String(value);
                if (stringValue.length > EXCEL_CELL_LIMIT) {
                    return stringValue.substring(0, EXCEL_CELL_LIMIT - 50) + '\n\n[... TRONCATO ...]';
                }
                return stringValue;
            };
            
            // Helper function to check if a field name is a date field
            const isDateField = (fieldName) => {
                const lowerField = String(fieldName).toLowerCase();
                return lowerField.includes('date') || lowerField.includes('data') || 
                       lowerField === 'event_date' || lowerField === 'admission_date' || 
                       lowerField === 'discharge_date';
            };
            
            // Sanitize clean data to ensure Excel compatibility
            const sanitizedCleanData = cleanData.map(row => {
                const sanitizedRow = {};
                Object.keys(row).forEach(key => {
                    try {
                        const value = row[key];
                        
                        if (value === null || value === undefined || value === '') {
                            sanitizedRow[key] = '';
                        } else if (value instanceof Date) {
                            sanitizedRow[key] = value.toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                            });
                        } else if (isDateField(key) && typeof value === 'number') {
                            // Format Excel serial date numbers
                            sanitizedRow[key] = formatExcelDate(value);
                        } else if (typeof value === 'object') {
                            // Convert complex objects to JSON string (truncated)
                            const jsonString = JSON.stringify(value);
                            sanitizedRow[key] = truncateForExcel(jsonString);
                        } else {
                            sanitizedRow[key] = truncateForExcel(value);
                        }
                    } catch (error) {
                        sanitizedRow[key] = '';
                    }
                });
                return sanitizedRow;
            });
            
            // Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(sanitizedCleanData);

            // Set column widths for better readability
            const columnWidths = [];
            if (cleanData.length > 0) {
                Object.keys(cleanData[0]).forEach(key => {
                    const maxLength = Math.max(
                        key.length,
                        ...cleanData.map(row => String(row[key] || '').length)
                    );
                    columnWidths.push({ wch: Math.min(maxLength + 2, 50) });
                });
            }
            worksheet['!cols'] = columnWidths;

            // Add worksheet to workbook
            const sheetName = `${importTypes[selectedType].name} - ${t('importableData', { ns: 'labels' })}`;
            // Truncate sheet name to Excel's 31-character limit
            const truncatedSheetName = sheetName.length > 31 ? sheetName.substring(0, 31) : sheetName;
            XLSX.utils.book_append_sheet(workbook, worksheet, truncatedSheetName);

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const filename = `${selectedType}_importable_data_${timestamp}.xlsx`;

            // Download the file
            XLSX.writeFile(workbook, filename);

            // Show success message
            MySwal.fire({
                icon: 'success',
                title: t('downloadCompleted', { ns: 'labels' }),
                text: t('cleanDataDownloaded', { ns: 'labels' }).replace('{filename}', filename),
                timer: 3000,
                showConfirmButton: false
            });

        } catch (error) {
            MySwal.fire({
                icon: 'error',
                title: t('downloadError', { ns: 'labels' }),
                text: t('errorGeneratingFile', { ns: 'labels' }),
                footer: `<small>Dettaglio tecnico: ${error.message}</small>`
            });
        }
    };

    // Render functions
    const renderTypeSelection = () => (
        <div className="card mb-4">
            <div className="card-header">
                <h5 className="card-title mb-0">
                    <i className="fa fa-list-alt me-2"></i>
                    1. {t('selectDataType', { ns: 'labels' })}
                </h5>
            </div>
            <div className="card-body">
                <div className="row g-3">
                    {Object.entries(importTypes).map(([key, type]) => (
                        <div key={key} className="col-12 col-md-6 col-lg-3">
                            <div 
                                className={`card h-100 ${selectedType === key ? `border-${type.color} border-3 shadow` : 'border-secondary'}`}
                                style={{ 
                                    cursor: 'pointer',
                                    backgroundColor: selectedType === key ? '#f8f9fa' : 'white'
                                }}
                                onClick={() => {
                                    setSelectedType(key);
                                    // Clear all previous analysis results when changing import type
                                    stopPolling(); // Stop any active polling
                                    setUploadedFile(null);
                                    setParsedData(null);
                                    setPreviewData(null);
                                    setProcessedData(null);
                                    setWarnings([]);
                                    setCleanData([]);
                                    setDirtyData([]);
                                    setDetectedProfile(null);
                                    setUploadedFileUID(null);
                                    setUploadProgress(0);
                                }}
                            >
                                <div className="card-body text-center">
                                    <i className={`fa ${type.icon} fa-2x text-${type.color} mb-2`}></i>
                                    <h6 className={`card-title text-${type.color} fw-bold`}>
                                        {type.name}
                                    </h6>
                                    <small className={selectedType === key ? 'text-secondary fw-bold' : 'text-muted'}>
                                        {type.requiredFields.length} {t('requiredFields', { ns: 'labels' })}
                                    </small>
                                    {selectedType === key && (
                                        <div className="mt-2">
                                            <span className={`badge bg-${type.color} text-white`}>
                                                <i className="fa fa-check me-1"></i>
                                                {t('selected', { ns: 'labels' })}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {selectedType && (
                    <div className="mt-3 p-3 border rounded bg-light">
                        <h6>{t('expectedColumns', { ns: 'labels' })} {importTypes[selectedType].name}:</h6>
                        <div className="mb-3">
                            <small className="text-info">
                                <i className="fa fa-info-circle me-1"></i>
                                {selectedType === 'hospitalization' 
                                    ? (t('hospitalizationMultiProfileInfo', { ns: 'labels' }) || 'Il sistema rileva automaticamente il formato del file. Colonne mostrate sono per riferimento (profilo STANDARD).')
                                    : t('excelColumnInfo', { ns: 'labels' })}
                            </small>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            {importTypes[selectedType].expectedExcelColumns.map((col, index) => {
                                // Map Italian to English to check if required
                                const englishCol = importTypes[selectedType].expectedColumns[index];
                                const isRequired = importTypes[selectedType].requiredFields.includes(englishCol);
                                const cardColor = importTypes[selectedType].color;
                                return (
                                    <span 
                                        key={index} 
                                        className={`badge ${isRequired ? `bg-${cardColor}` : 'bg-secondary'} text-white position-relative`}
                                        title={selectedType === 'hospitalization' ? `${col}` : `Equivale a: ${englishCol}`}
                                    >
                                        {col} 
                                        {isRequired && <span style={{ color: '#ff6b6b' }} className="fw-bold"> *</span>}
                                    </span>
                                );
                            })}
                        </div>
                        <small className="text-muted mt-2 d-block">
                            <span style={{ color: '#ff6b6b' }} className="fw-bold">*</span> {t('requiredField', { ns: 'labels' })}                            
                        </small>
                    </div>
                )}
            </div>
        </div>
    );

    const renderFileUpload = () => (
        <div className="card mb-4">
            <div className="card-header">
                <h5 className="card-title mb-0">
                    <i className="fa fa-file-excel me-2"></i>
                    2. {t('uploadExcelFile', { ns: 'labels' })}
                </h5>
            </div>
            <div className="card-body">
                <div 
                    className="border-2 border-dashed border-primary rounded p-4 text-center"
                    style={{ minHeight: '150px' }}
                    onDrop={(e) => {
                        e.preventDefault();
                        const files = e.dataTransfer.files;
                        if (files.length > 0) {
                            handleFileUpload(files[0]);
                        }
                    }}
                    onDragOver={(e) => e.preventDefault()}
                >
                    {!uploadedFile ? (
                        <>
                            <i className="fa fa-cloud-upload-alt fa-3x text-primary mb-3"></i>
                            <h5>{t('dragDropFile', { ns: 'labels' })}</h5>
                            <p className="text-muted">{t('supportedFormats', { ns: 'labels' })}</p>
                            <input
                                type="file"
                                className="d-none"
                                accept=".xlsx,.xls"
                                id="fileInput"
                                onChange={(e) => {
                                    if (e.target.files.length > 0) {
                                        handleFileUpload(e.target.files[0]);
                                    }
                                }}
                                disabled={!selectedType}
                            />
                            <button 
                                className="btn btn-primary"
                                onClick={() => document.getElementById('fileInput').click()}
                                disabled={!selectedType}
                            >
                                {t('selectFile', { ns: 'labels' })}
                            </button>
                        </>
                    ) : (
                        <div>
                            <i className="fa fa-file-excel fa-3x text-success mb-3"></i>
                            <h5 className="text-success">{uploadedFile.name}</h5>
                            <p className="text-muted">
                                {t('fileSize', { ns: 'labels' })}: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            <button 
                                className="btn btn-outline-danger me-2"
                                onClick={() => {
                                    stopPolling(); // Stop any active polling
                                    setUploadedFile(null);
                                    setParsedData(null);
                                    setPreviewData(null);
                                    // Clear analysis results from previous interactions
                                    setProcessedData(null);
                                    setWarnings([]);
                                    setCleanData([]);
                                    setDirtyData([]);
                                    setDetectedProfile(null);
                                    setUploadedFileUID(null);
                                    setUploadProgress(0);
                                }}
                            >
                                {t('removeFile', { ns: 'labels' })}
                            </button>
                            
                            {/* Show upload progress if uploading */}
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <div className="mt-3">
                                    <h6>
                                        {t('uploadingFile', { ns: 'labels' }) || 'Uploading file...'}
                                        <span className="float-end">{uploadProgress}%</span>
                                    </h6>
                                    <div className="progress" style={{ height: '20px' }}>
                                        <div 
                                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                                            role="progressbar" 
                                            style={{ width: `${uploadProgress}%` }}
                                            aria-valuenow={uploadProgress}
                                            aria-valuemin="0" 
                                            aria-valuemax="100"
                                        >
                                            {uploadProgress}%
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {parsedData && uploadProgress === 100 && (
                                <button 
                                    className="btn btn-primary"
                                    onClick={preAnalyzeData}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            {t('analysisInProgress', { ns: 'labels' })}
                                        </>
                                    ) : (
                                        t('startPreAnalysis', { ns: 'labels' })
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Detected Profile Info - for hospitalization */}
                {selectedType === 'hospitalization' && detectedProfile && (
                    <div className="mt-3">
                        <div className="alert alert-info">
                            <h6 className="alert-heading">
                                <i className="fa fa-info-circle me-2"></i>
                                {t('detectedProfile', { ns: 'labels' }) || 'Profilo Rilevato'}
                            </h6>
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                    <strong>{detectedProfile}</strong>
                                    <p className="mb-0 small text-muted mt-1">
                                        {detectedProfile === 'ASLROMA1' 
                                            ? t('profileAslRoma1Description', { ns: 'labels' }) || 'Il backend utilizzerà il mapping colonne specifico per ASL Roma 1'
                                            : t('profileStandardDescription', { ns: 'labels' }) || 'Il backend utilizzerà il mapping colonne standard'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Progress Bar - appears right after the buttons */}
                {(isPolling || progressData || progressId) && (
                    <div className="mt-3">
                        <div className="card border-info shadow-sm">
                            <div className="card-header bg-info text-white">
                                <h6 className="card-title mb-0">
                                    <i className="fa fa-chart-line me-2"></i>
                                    {progressData?.taskType?.includes('_save') 
                                        ? (t('importProgress', { ns: 'labels' }) || 'Import Progress')
                                        : t('analysisProgress', { ns: 'labels' })}
                                </h6>
                            </div>
                            <div className="card-body">
                                {progressData ? (
                                    <>
                                        {/* Progress Bar */}
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="fw-bold">
                                                    {t('progress', { ns: 'labels' })}: {progressData.percentage || 0}%
                                                </span>
                                                <span className="text-muted">
                                                    {progressData.processedItems || 0} / {progressData.totalItems || 0} {t('rows', { ns: 'labels' })}
                                                </span>
                                            </div>
                                            <div className="progress" style={{ height: '20px' }}>
                                                <div 
                                                    className={`progress-bar ${progressData.status === 'completed' ? 'bg-success' : progressData.status === 'error' ? 'bg-danger' : 'progress-bar-striped progress-bar-animated bg-info'}`} 
                                                    role="progressbar" 
                                                    style={{ width: `${progressData.percentage || 0}%` }}
                                                    aria-valuenow={progressData.percentage || 0}
                                                    aria-valuemin="0" 
                                                    aria-valuemax="100"
                                                >
                                                    {progressData.status === 'completed' ? (
                                                        <>
                                                            <i className="fa fa-check me-1"></i>
                                                            {t('taskCompleted', { ns: 'labels' })}
                                                        </>
                                                    ) : progressData.status === 'error' ? (
                                                        <>
                                                            <i className="fa fa-times me-1"></i>
                                                            {t('taskFailed', { ns: 'labels' })}
                                                        </>
                                                    ) : (
                                                        `${progressData.percentage || 0}%`
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Current Status */}
                                        <div className={`alert ${progressData.status === 'completed' ? 'alert-success' : progressData.status === 'error' ? 'alert-danger' : 'alert-info'} mb-3`}>
                                            <div className="d-flex align-items-center">
                                                {progressData.status === 'completed' ? (
                                                    <div className="me-2">
                                                        <i className="fa fa-check-circle text-success" style={{ fontSize: '1.2rem' }}></i>
                                                    </div>
                                                ) : progressData.status === 'error' ? (
                                                    <div className="me-2">
                                                        <i className="fa fa-times-circle text-danger" style={{ fontSize: '1.2rem' }}></i>
                                                    </div>
                                                ) : (
                                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                                        <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                                                    </div>
                                                )}
                                                <div>
                                                    <strong>{t('currentStatus', { ns: 'labels' })}: </strong>
                                                    {progressData.currentStep || 
                                                        (progressData.status === 'completed' ? t('taskCompleted', { ns: 'labels' }) : 
                                                         progressData.status === 'error' ? t('taskFailed', { ns: 'labels' }) : 
                                                         t('processing', { ns: 'labels' }))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Real-time Warnings */}
                                        {progressData.warnings && progressData.warnings.length > 0 && (
                                            <div className="alert alert-warning">
                                                <h6 className="alert-heading">
                                                    <i className="fa fa-exclamation-triangle me-1"></i>
                                                    {t('realtimeWarnings', { ns: 'labels' })} ({progressData.warnings.length})
                                                </h6>
                                                <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                                    {progressData.warnings.slice(-3).map((warning, index) => (
                                                        <div key={index} className="small">
                                                            <strong>{t('row', { ns: 'labels' })} {warning.row_number}:</strong> {warning.warn_description}
                                                        </div>
                                                    ))}
                                                    {progressData.warnings.length > 3 && (
                                                        <div className="small text-muted mt-1">
                                                            ... {t('andMore', { ns: 'labels' }).replace('{count}', progressData.warnings.length - 3)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    // Fallback when polling is active but no progress data yet
                                    <div className="text-center">
                                        <div className="mb-2">
                                            <div className="progress" style={{ height: '20px' }}>
                                                <div 
                                                    className="progress-bar progress-bar-striped progress-bar-animated bg-info" 
                                                    role="progressbar" 
                                                    style={{ width: '100%' }}
                                                >
                                                    {t('initializingAnalysis', { ns: 'labels' }) || 'Initializing...'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="alert alert-info mb-0">
                                            <div className="d-flex align-items-center">
                                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                                    <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                                                </div>
                                                <div>
                                                    <strong>{t('currentStatus', { ns: 'labels' })}: </strong>
                                                    {t('initializingAnalysis', { ns: 'labels' }) || 'Initializing analysis...'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderPreview = () => {
        if (!previewData || !selectedType) return null;

        // Show the Italian column names as they appear in the Excel file
        const excelColumns = importTypes[selectedType].expectedExcelColumns;
        const englishColumns = importTypes[selectedType].expectedColumns;
        
        return (
            <div className="card mb-4">
                <div className="card-header">
                <h5 className="card-title mb-0">
                    <i className="fa fa-eye me-2"></i>
                    3. {t('dataPreview', { ns: 'labels' })} ({t('firstRows', { ns: 'labels' })})
                </h5>
                </div>
                <div className="card-body">
                    <div className="alert alert-info mb-3">
                        <i className="fa fa-info-circle me-2"></i>
                        <strong>{t('previewColumns', { ns: 'labels' })}:</strong> {t('previewDescription', { ns: 'labels' })}
                    </div>
                    <div className="table-responsive">
                        <table className="table table-striped table-sm">
                            <thead className="table-dark">
                                <tr>
                                    <th style={{ minWidth: '50px' }}>#</th>
                                    {/* Show all columns from the actual Excel data */}
                                    {Object.keys(previewData[0] || {}).map((col, index) => {
                                        // Try to find if this column corresponds to a required field
                                        const excelIndex = excelColumns.findIndex(excCol => excCol === col);
                                        const isRequired = excelIndex >= 0 ? 
                                            importTypes[selectedType].requiredFields.includes(englishColumns[excelIndex]) : false;
                                        
                                        return (
                                            <th key={index} className={isRequired ? 'text-warning fw-bold' : ''} style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>
                                                {col}
                                                {isRequired && <span style={{ color: '#ff6b6b' }} className="fw-bold"> *</span>}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {previewData.map((row, index) => (
                                    <tr key={index}>
                                        <td style={{ minWidth: '50px' }}>{index + 1}</td>
                                        {Object.keys(previewData[0] || {}).map((col, colIndex) => (
                                            <td key={colIndex} style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                    const cellValue = row[col];
                                                    // Check if this is a date column and value is Excel serial number
                                                    if ((col === 'EVENT_DATE' || col.includes('DATE') || col.includes('_DATE')) && 
                                                        typeof cellValue === 'number' && cellValue > 0 && cellValue < 100000) {
                                                        return formatExcelDate(cellValue);
                                                    }
                                                    return cellValue !== undefined ? String(cellValue).substring(0, 30) : '-';
                                                })()}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <small className="text-muted">
                        {t('totalRows', { ns: 'labels' })}: {parsedData?.length || 0} | 
                        {t('detectedColumns', { ns: 'labels' })}: {Object.keys(previewData[0] || {}).length}
                    </small>
                </div>
            </div>
        );
    };

    const renderStatisticsPanel = () => {
        // Only show if we have processed data
        if (!processedData && !warnings.length) return null;
        
        const stats = importStats;
        const hasData = stats.totalRowsInExcel > 0;
        
        if (!hasData) return null;
        
        // Check if this is hospitalization import with event-to-stay conversion
        const isHospitalization = selectedType === 'hospitalization' && stats.validatedEvents;
        
        return (
            <div className="card mb-4">
                <div className="card-header">
                    <h5 className="card-title mb-0">
                        <i className="fa fa-chart-bar me-2"></i>
                        {t('importStatistics', { ns: 'labels' }) || 'Statistiche Importazione'}
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        {/* Excel Data */}
                        <div className="col-md-4 mb-3">
                            <div className="card bg-light h-100">
                                <div className="card-body">
                                    <h6 className="card-subtitle mb-3 text-muted">
                                        <i className="fa fa-file-excel me-1"></i>
                                        {t('excelFile', { ns: 'labels' }) || 'File Excel'}
                                    </h6>
                                    <div className="mb-2 d-flex justify-content-between align-items-center">
                                        <strong className="text-nowrap">{t('totalRows', { ns: 'labels' }) || 'Righe Totali'}:</strong>
                                        <span className="ms-2">{stats.totalRowsInExcel.toLocaleString()}</span>
                                    </div>
                                    <div className="mb-2 d-flex justify-content-between align-items-center">
                                        <strong className="text-nowrap">{t('emptyRowsFiltered', { ns: 'labels' }) || 'Righe Vuote Filtrate'}:</strong>
                                        <span className="ms-2">{stats.emptyRowsFiltered.toLocaleString()}</span>
                                    </div>
                                    <div className="border-top pt-2 d-flex justify-content-between align-items-center">
                                        <strong className="text-nowrap">{t('sentToBackend', { ns: 'labels' }) || 'Inviate al Backend'}:</strong>
                                        <span className="ms-2">{stats.rowsSentToBackend.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Backend Processing */}
                        <div className="col-md-4 mb-3">
                            <div className="card bg-light h-100">
                                <div className="card-body">
                                    <h6 className="card-subtitle mb-3 text-muted">
                                        <i className="fa fa-server me-1"></i>
                                        {t('backendProcessing', { ns: 'labels' }) || 'Elaborazione Backend'}
                                    </h6>
                                    <div className="mb-2 d-flex justify-content-between align-items-center">
                                        <strong className="text-nowrap">{t('cleanRows', { ns: 'labels' }) || 'Righe Valide'}:</strong>
                                        <span className="ms-2">{stats.cleanRowsReceived.toLocaleString()}</span>
                                    </div>
                                    <div className="mb-2 d-flex justify-content-between align-items-center">
                                        <strong className="text-nowrap">{t('dirtyRows', { ns: 'labels' }) || 'Righe con Errori'}:</strong>
                                        <span className="ms-2">{stats.dirtyRowsReceived.toLocaleString()}</span>
                                    </div>
                                    <div className="border-top pt-2 d-flex justify-content-between align-items-center">
                                        <strong className="text-nowrap">{t('totalProcessed', { ns: 'labels' }) || 'Totale Elaborate'}:</strong>
                                        <span className="ms-2">{stats.backendProcessedTotal.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Row Tracking */}
                        <div className="col-md-4 mb-3">
                            <div className={`card h-100 ${stats.missingRows > 0 ? 'border-warning border-2' : 'bg-light'}`}>
                                <div className="card-body">
                                    <h6 className="card-subtitle mb-3 text-muted">
                                        <i className="fa fa-calculator me-1"></i>
                                        {t('rowTracking', { ns: 'labels' }) || 'Tracciamento Righe'}
                                    </h6>
                                    <div className="mb-2 d-flex justify-content-between align-items-center">
                                        <strong className="text-nowrap">{t('sentToBackend', { ns: 'labels' }) || 'Inviate'}:</strong>
                                        <span className="ms-2">{stats.rowsSentToBackend.toLocaleString()}</span>
                                    </div>
                                    <div className="mb-2 d-flex justify-content-between align-items-center">
                                        <strong className="text-nowrap">{t('received', { ns: 'labels' }) || 'Ricevute'}:</strong>
                                        <span className="ms-2">{stats.backendProcessedTotal.toLocaleString()}</span>
                                    </div>
                                    <div className={`border-top pt-2 d-flex justify-content-between align-items-center ${stats.missingRows > 0 ? 'text-warning fw-bold' : ''}`}>
                                        <strong className="text-nowrap">
                                            {stats.missingRows > 0 ? (
                                                <><i className="fa fa-exclamation-triangle me-1"></i>{t('missingRows', { ns: 'labels' }) || 'Righe Mancanti'}:</>
                                            ) : (
                                                <><i className="fa fa-check-circle me-1"></i>{t('allRowsAccounted', { ns: 'labels' }) || 'Tutte'}:</>
                                            )}
                                        </strong>
                                        <span className="ms-2">
                                            {stats.missingRows.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Special message for hospitalization explaining event-to-stay conversion */}
                    {isHospitalization && (
                        <div className="alert alert-info mb-3">
                            <h6 className="alert-heading">
                                <i className="fa fa-info-circle me-2"></i>
                                {t('hospitalizationConversionInfo', { ns: 'labels' }) || 'Trasformazione Eventi → Periodi di Degenza'}
                            </h6>
                            <p className="mb-2">
                                {t('hospitalizationConversionDescription', { ns: 'labels' }) || 
                                    'Gli eventi di degenza (ADM, TNF, DSC) vengono trasformati in periodi di degenza.'}
                            </p>
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    <strong>{t('inputEvents', { ns: 'labels' }) || 'Eventi Input'}:</strong>
                                    <ul className="mb-0 mt-2">
                                        <li className="mb-2">{t('validatedEvents', { ns: 'labels' }) || 'Eventi Validati'}: {stats.validatedEvents?.toLocaleString()}</li>
                                        <li>{t('rejectedEvents', { ns: 'labels' }) || 'Eventi Rifiutati'}: {stats.rejectedEvents?.toLocaleString()}</li>
                                    </ul>
                                </div>
                                <div className="col-md-6">
                                    <strong>{t('outputStays', { ns: 'labels' }) || 'Degenze Output'}:</strong>
                                    <ul className="mb-0 mt-2">
                                        <li className="mb-2">{t('staysGenerated', { ns: 'labels' }) || 'Degenze Generate'}: {stats.staysGenerated?.toLocaleString()}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Warning message if rows are missing (only for non-hospitalization or real discrepancies) */}
                    {stats.missingRows > 0 && !isHospitalization && (
                        <div className="alert alert-warning mb-0">
                            <h6 className="alert-heading">
                                <i className="fa fa-exclamation-triangle me-2"></i>
                                {t('missingRowsWarning', { ns: 'labels' }) || 'Attenzione: Righe Mancanti'}
                            </h6>
                            <p className="mb-2">
                                {t('missingRowsDescription', { ns: 'labels' }) || 
                                    'Alcune righe non sono state elaborate dal backend. Controlla i log della console per dettagli.'}
                            </p>
                            <small className="text-muted">
                                <strong>{t('possibleReasons', { ns: 'labels' }) || 'Possibili cause'}:</strong>
                                <ul className="mb-0 mt-1">
                                    <li>{t('missingReason1', { ns: 'labels' }) || 'Righe duplicate filtrate dal backend'}</li>
                                    <li>{t('missingReason2', { ns: 'labels' }) || 'Righe con dati non validi scartate senza warning'}</li>
                                    <li>{t('missingReason3', { ns: 'labels' }) || 'Filtri di business logic applicati dal backend'}</li>
                                </ul>
                            </small>
                        </div>
                    )}
                    
                    {/* Success message if all rows are accounted for */}
                    {stats.missingRows === 0 && stats.rowsSentToBackend > 0 && (
                        <div className="alert alert-success mb-0">
                            <i className="fa fa-check-circle me-2"></i>
                            <strong>{t('allRowsAccountedSuccess', { ns: 'labels' }) || 'Tutte le righe sono state elaborate correttamente!'}</strong>
                            <p className="mb-0 small">
                                {stats.cleanRowsReceived} {t('rowsReadyForImport', { ns: 'labels' }) || 'righe pronte per l\'importazione'}, {' '}
                                {stats.dirtyRowsReceived} {t('rowsWithErrors', { ns: 'labels' }) || 'righe con errori'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderResults = () => {
        if (!processedData && !warnings.length) return null;

        return (
            <div className="row">
                {/* Clean Data */}
                <div className="col-md-6">
                    <div className="card mb-4 d-flex flex-column responsive-card" style={{ height: '600px' }}>
                        <div className="card-header bg-success text-white">
                            <h5 className="card-title mb-0">
                                <i className="fa fa-check-circle me-2"></i>
                                {t('importableRows', { ns: 'labels' })}: {cleanData.length}
                            </h5>
                        </div>
                        <div className="card-body d-flex flex-column" style={{ flex: '1', overflow: 'hidden' }}>
                            {cleanData.length > 0 ? (
                                <>
                                    {/* Pagination Info */}
                                    <div className="d-flex justify-content-start align-items-center mb-3">
                                        <div className="text-muted">
                                            <small>
                                                {t('showing', { ns: 'labels' }) || 'Showing'} {' '}
                                                {((cleanDataCurrentPage - 1) * rowsPerPage) + 1} - {' '}
                                                {Math.min(cleanDataCurrentPage * rowsPerPage, cleanData.length)} {' '}
                                                {t('of', { ns: 'labels' }) || 'of'} {cleanData.length} {' '}
                                                {t('records', { ns: 'labels' }) || 'records'}
                                            </small>
                                        </div>
                                    </div>

                                    <div className="table-responsive responsive-table-container" style={{ height: '350px', overflowX: 'auto', overflowY: 'auto' }}>
                                        <table className="table table-sm table-nowrap table-uniform-height">
                                            <thead className="table-light sticky-top">
                                                <tr>
                                                    <th style={{ minWidth: '50px', height: '40px', verticalAlign: 'middle' }}>#</th>
                                                    {Object.keys(cleanData[0] || {}).map((col, index) => (
                                                        <th key={index} style={{ minWidth: '120px', whiteSpace: 'nowrap', height: '40px', verticalAlign: 'middle' }}>
                                                            {col}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getPaginatedData(cleanData, cleanDataCurrentPage, rowsPerPage).map((row, index) => {
                                                    const actualRowNumber = ((cleanDataCurrentPage - 1) * rowsPerPage) + index + 1;
                                                    return (
                                                        <tr key={actualRowNumber} style={{ height: '40px' }}>
                                                            <td style={{ minWidth: '50px', height: '40px', verticalAlign: 'middle' }}>{actualRowNumber}</td>
                                                            {Object.keys(cleanData[0] || {}).map((col, colIndex) => (
                                                                <td key={colIndex} style={{ minWidth: '120px', whiteSpace: 'nowrap', height: '40px', verticalAlign: 'middle' }}>
                                                                    {row[col] !== undefined ? String(row[col]).substring(0, 30) : '-'}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Bottom Pagination */}
                                    <div className="d-flex justify-content-center mt-3" style={{ minHeight: '40px' }}>
                                        {getTotalPages(cleanData.length, rowsPerPage) > 1 ? (
                                            renderPagination(
                                                cleanDataCurrentPage, 
                                                getTotalPages(cleanData.length, rowsPerPage), 
                                                setCleanDataCurrentPage,
                                                'clean-data-bottom'
                                            )
                                        ) : (
                                            <div></div>
                                        )}
                                    </div>
                                    
                                    {/* Bottom Actions */}
                                    <div className="d-flex gap-2 mt-auto" style={{ minHeight: '40px' }}>
                                        <button 
                                            className="btn btn-success flex-grow-1"
                                            onClick={importCleanData}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    {t('importInProgress', { ns: 'labels' })}
                                                </>
                                            ) : (
                                                t('importCleanRecords', { ns: 'labels' }).replace('{count}', cleanData.length)
                                            )}
                                        </button>
                                        <button 
                                            className="btn btn-outline-success"
                                            onClick={downloadCleanDataAsExcel}
                                            title={t('downloadImportableData', { ns: 'labels' })}
                                        >
                                            <i className="fa fa-file-excel"></i>
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <p className="text-muted">{t('noCleanData', { ns: 'labels' })}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Dirty Data */}
                <div className="col-md-6">
                    <div className="card mb-4 d-flex flex-column responsive-card" style={{ height: '600px' }}>
                        <div className="card-header bg-danger text-white">
                            <h5 className="card-title mb-0">
                                <i className="fa fa-exclamation-triangle me-2"></i>
                                {t('rowsWithErrors', { ns: 'labels' })}: {dirtyData.length}
                            </h5>
                        </div>
                        <div className="card-body d-flex flex-column" style={{ flex: '1', overflow: 'hidden' }}>
                            {dirtyData.length > 0 ? (
                                <>
                                    {/* Pagination Info */}
                                    <div className="d-flex justify-content-start align-items-center mb-3">
                                        <div className="text-muted">
                                            <small>
                                                {t('showing', { ns: 'labels' }) || 'Showing'} {' '}
                                                {((dirtyDataCurrentPage - 1) * rowsPerPage) + 1} - {' '}
                                                {Math.min(dirtyDataCurrentPage * rowsPerPage, dirtyData.length)} {' '}
                                                {t('of', { ns: 'labels' }) || 'of'} {dirtyData.length} {' '}
                                                {t('errors', { ns: 'labels' }) || 'errors'}
                                            </small>
                                        </div>
                                    </div>

                                    <div className="table-responsive responsive-table-container" style={{ height: '350px', overflowX: 'auto', overflowY: 'auto' }}>
                                        <table className="table table-sm table-nowrap table-uniform-height">
                                            <thead className="table-light sticky-top">
                                                <tr>
                                                    <th style={{ width: '60px', padding: '0.25rem 0.5rem', height: '40px', verticalAlign: 'middle' }}>#</th>
                                                    <th style={{ width: 'auto', padding: '0.25rem 0.5rem', height: '40px', verticalAlign: 'middle' }}>
                                                        {t('error', { ns: 'labels' })}
                                                    </th>
                                                    <th style={{ width: '150px', padding: '0.25rem 0.5rem', height: '40px', verticalAlign: 'middle' }}>
                                                        {t('errorCode', { ns: 'labels' })}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getPaginatedData(dirtyData, dirtyDataCurrentPage, rowsPerPage).map((error, index) => (
                                                    <tr key={index} style={{ height: '40px' }}>
                                                        <td style={{ width: '60px', padding: '0.25rem 0.5rem', textAlign: 'center', height: '40px', verticalAlign: 'middle' }}>{error.row}</td>
                                                        <td style={{ width: 'auto', padding: '0.25rem 0.5rem', height: '40px', verticalAlign: 'middle' }}>
                                                            <small className="text-danger" style={{ lineHeight: '1.2' }}>
                                                                {error.error}
                                                            </small>
                                                        </td>
                                                        <td style={{ width: '150px', padding: '0.25rem 0.5rem', height: '40px', verticalAlign: 'middle' }}>
                                                            <small className="text-muted">
                                                                {error.code}
                                                            </small>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Bottom Pagination */}
                                    <div className="d-flex justify-content-center mt-3" style={{ minHeight: '40px' }}>
                                        {getTotalPages(dirtyData.length, rowsPerPage) > 1 ? (
                                            renderPagination(
                                                dirtyDataCurrentPage, 
                                                getTotalPages(dirtyData.length, rowsPerPage), 
                                                setDirtyDataCurrentPage,
                                                'dirty-data-bottom'
                                            )
                                        ) : (
                                            <div></div>
                                        )}
                                    </div>
                                    
                                    {/* Bottom Actions */}
                                    <div className="d-flex justify-content-center mt-auto" style={{ minHeight: '40px' }}>
                                        <button 
                                            className="btn btn-outline-danger"
                                            onClick={downloadDirtyDataAsExcel}
                                        >
                                            <i className="fa fa-file-excel me-2"></i>
                                            {t('downloadErrorReport', { ns: 'labels' })}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <p className="text-muted">{t('noErrorsDetected', { ns: 'labels' })}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render save progress bar (appears AFTER results tables)
    const renderSaveProgress = () => {
        // Only show during save operation
        const isSaveInProgress = progressData?.taskType?.includes('_save');
        
        if (!isSaveInProgress) return null;

        return (
            <div className="mt-4" id="save-progress-bar">
                <div className="card border-success shadow-lg">
                    <div className="card-header bg-success text-white">
                        <h5 className="card-title mb-0">
                            <i className="fa fa-database me-2"></i>
                            {t('importProgress', { ns: 'labels' }) || 'Progresso Importazione'}
                        </h5>
                    </div>
                    <div className="card-body">
                        {progressData ? (
                            <>
                                {/* Progress Bar */}
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="fw-bold">
                                            {t('progress', { ns: 'labels' })}: {progressData.percentage || 0}%
                                        </span>
                                        <span className="text-muted">
                                            {progressData.processedItems || 0} / {progressData.totalItems || 0} {t('rows', { ns: 'labels' })}
                                        </span>
                                    </div>
                                    <div className="progress" style={{ height: '25px' }}>
                                        <div 
                                            className={`progress-bar ${progressData.status === 'completed' ? 'bg-success' : progressData.status === 'error' ? 'bg-danger' : 'progress-bar-striped progress-bar-animated bg-success'}`} 
                                            role="progressbar" 
                                            style={{ width: `${progressData.percentage || 0}%` }}
                                            aria-valuenow={progressData.percentage || 0}
                                            aria-valuemin="0" 
                                            aria-valuemax="100"
                                        >
                                            {progressData.status === 'completed' ? (
                                                <>
                                                    <i className="fa fa-check me-1"></i>
                                                    {t('importCompleted', { ns: 'labels' })}
                                                </>
                                            ) : progressData.status === 'error' ? (
                                                <>
                                                    <i className="fa fa-times me-1"></i>
                                                    {t('importFailed', { ns: 'labels' })}
                                                </>
                                            ) : (
                                                `${progressData.percentage || 0}%`
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Current Status */}
                                <div className={`alert ${progressData.status === 'completed' ? 'alert-success' : progressData.status === 'error' ? 'alert-danger' : 'alert-info'} mb-0`}>
                                    <div className="d-flex align-items-center">
                                        {progressData.status === 'completed' ? (
                                            <div className="me-2">
                                                <i className="fa fa-check-circle text-success" style={{ fontSize: '1.5rem' }}></i>
                                            </div>
                                        ) : progressData.status === 'error' ? (
                                            <div className="me-2">
                                                <i className="fa fa-times-circle text-danger" style={{ fontSize: '1.5rem' }}></i>
                                            </div>
                                        ) : (
                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                                            </div>
                                        )}
                                        <div>
                                            <strong>{t('currentStatus', { ns: 'labels' })}: </strong>
                                            {progressData.currentStep || 
                                                (progressData.status === 'completed' ? t('importCompleted', { ns: 'labels' }) : 
                                                 progressData.status === 'error' ? t('importFailed', { ns: 'labels' }) : 
                                                 t('importing', { ns: 'labels' }))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center">
                                <div className="spinner-border text-success mb-2" role="status">
                                    <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                                </div>
                                <p className="text-muted mb-0">{t('initializingImport', { ns: 'labels' }) || 'Inizializzazione importazione...'}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>
            {/* Header */}
            <header className="bg-white shadow-sm">
                <div className="container-fluid d-flex justify-content-between align-items-center py-2">
                    <ol className="breadcrumb mb-0"></ol>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-grow-1 overflow-auto">
                <div className="container-fluid py-4 main-content-container">
                    <div className="row mb-4">
                        <div className="col-12">
                            <h2 className="text-center text-primary">
                                <i className="fa fa-file-import me-2"></i>
                                {t('importHealthData', { ns: 'labels' })}
                            </h2>
                            <p className="text-center text-muted">
                                {t('importValidateDescription', { ns: 'labels' })}
                            </p>                            
                        </div>
                    </div>

                    {/* Type Selection */}
                    {renderTypeSelection()}

                    {/* File Upload */}
                    {selectedType && renderFileUpload()}

                    {/* Preview */}
                    {renderPreview()}

                    {/* Statistics Panel */}
                    {renderStatisticsPanel()}

                    {/* Results */}
                    {renderResults()}

                    {/* Save Progress Bar - appears BELOW results tables */}
                    {renderSaveProgress()}

                    {/* Loading Overlay - only show if we're loading but not polling (i.e., for file uploads, etc.) */}
                    {loading && !isPolling && !progressId && (
                        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50" style={{ zIndex: 9999 }}>
                            <div className="text-center text-white">
                                <div className="spinner-border mb-3" style={{ width: '3rem', height: '3rem' }}></div>
                                <h4>{t('processingInProgress', { ns: 'labels' })}</h4>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Custom Styles */}
            <style>{`
                .card:hover {
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    transition: box-shadow 0.3s ease;
                }
                
                .border-dashed {
                    border-style: dashed !important;
                }
                
                .hover-shadow:hover {
                    box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
                    transition: all 0.3s ease;
                }
                
                .sticky-top {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                
                .table-nowrap {
                    white-space: nowrap;
                }
                
                .table-compact {
                    table-layout: fixed;
                }
                
                .table-compact td,
                .table-compact th {
                    padding: 0.25rem 0.5rem !important;
                    vertical-align: middle;
                }
                
                .table-compact-auto {
                    table-layout: auto;
                    width: 100%;
                }
                
                .table-compact-auto td,
                .table-compact-auto th {
                    padding: 0.25rem 0.5rem !important;
                    vertical-align: middle;
                }
                
                .table-responsive {
                    scrollbar-width: thin;
                    scrollbar-color: #6c757d #f8f9fa;
                }
                
                .table-responsive::-webkit-scrollbar {
                    height: 8px;
                    width: 8px;
                }
                
                .table-responsive::-webkit-scrollbar-track {
                    background: #f8f9fa;
                    border-radius: 4px;
                }
                
                .table-responsive::-webkit-scrollbar-thumb {
                    background: #6c757d;
                    border-radius: 4px;
                }
                
                .table-responsive::-webkit-scrollbar-thumb:hover {
                    background: #495057;
                }
                
                .table-responsive::-webkit-scrollbar-corner {
                    background: #f8f9fa;
                }
                
                /* Pagination Styles */
                .pagination .page-link {
                    border-radius: 0.25rem;
                    margin: 0 2px;
                    border: 1px solid #dee2e6;
                    color: #6c757d;
                    padding: 0.375rem 0.75rem;
                    font-size: 0.875rem;
                }
                
                .pagination .page-link:hover {
                    background-color: #e9ecef;
                    border-color: #adb5bd;
                    color: #495057;
                }
                
                .pagination .page-item.active .page-link {
                    background-color: #007bff;
                    border-color: #007bff;
                    color: white;
                }
                
                .pagination .page-item.disabled .page-link {
                    color: #6c757d;
                    background-color: #fff;
                    border-color: #dee2e6;
                    cursor: not-allowed;
                }
                
                .pagination-sm .page-link {
                    padding: 0.25rem 0.5rem;
                    font-size: 0.75rem;
                }
                
                /* Uniform table row heights */
                .table-uniform-height th,
                .table-uniform-height td {
                    height: 40px !important;
                    vertical-align: middle !important;
                    line-height: 1.2;
                }
                
                .table-uniform-height tbody tr {
                    height: 40px !important;
                }
                
                .table-uniform-height thead tr {
                    height: 40px !important;
                }
                
                /* Prevent content from overlapping with fixed toolbars */
                .main-content-container {
                    padding-bottom: 80px !important;
                    min-height: calc(100vh - 120px);
                }
                
                /* Responsive card heights */
                @media (max-width: 768px) {
                    .responsive-card {
                        max-height: calc(100vh - 150px) !important;
                        min-height: 400px !important;
                    }
                    
                    .responsive-table-container {
                        height: calc(100vh - 350px) !important;
                        max-height: 400px !important;
                        min-height: 250px !important;
                    }
                }
            `}</style>
        </div>
    );
};

export { DataImportScreen };
